import { useNavigate } from 'react-router';
import { oublierIdentite } from '../../auth/identite.js';
import { chargerSession } from '../../auth/session.js';
import { useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import { useShellContext } from '../shell/context.js';
import { useEnLigne } from '../racine/BandeauHorsLigne.js';
import { persistanceHorsLigne } from '../../horsLigne/index.js';
import { preparerAppareil, type ProgressionPreparation } from '../../horsLigne/preparation.js';
import { modeDemonstrationActif } from '../../api/demo.js';
import styles from './HorsLignePage.module.css';

export function HorsLignePage(): JSX.Element {
  const { galerie, galerieSlug, session } = useShellContext();
  const { t, i18n } = useTranslation('regie');
  const client = useQueryClient();
  const navigate = useNavigate();
  const enLigne = useEnLigne();
  const demo = modeDemonstrationActif();
  const reglage = galerie.horsLigne;
  const [progression, setProgression] = useState<ProgressionPreparation | null>(null);
  const [message, setMessage] = useState('');
  const etat = useQuery({
    queryKey: ['horsLigne', session.compte.id, galerieSlug, 'etat'],
    queryFn: () => persistanceHorsLigne().etat(),
    enabled: !demo,
    networkMode: 'always',
  });
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const date = (valeur: string) => formatDate(new Date(valeur), langue, 'long');
  async function preparer(): Promise<void> {
    if (!reglage || demo) return;
    setMessage('');
    setProgression({ faites: 0, total: 0 });
    try {
      const fraiche = await chargerSession(galerieSlug);
      const autorise = fraiche.galeries.find((g) => g.slug === galerieSlug)?.horsLigne;
      if (!autorise) throw new Error('autorisation retirée');
      await preparerAppareil(client, galerieSlug, autorise.perimetre, setProgression);
      const issue = await persistanceHorsLigne().enregistrer(client, galerieSlug);
      if (issue === 'trop_volumineux') setMessage(t('reglages.horsLigne.trop'));
      else if (issue !== 'enregistre') setMessage(t('reglages.horsLigne.erreur'));
      await etat.refetch();
    } catch {
      setMessage(t('reglages.horsLigne.erreur'));
    } finally {
      setProgression(null);
    }
  }
  async function effacer(): Promise<void> {
    try {
      if (session.consultationHorsLigne) {
        await oublierIdentite(client);
        await navigate('/connexion', { replace: true });
        return;
      }
      await persistanceHorsLigne().purgerTout();
      await etat.refetch();
      setMessage(t('reglages.horsLigne.efface'));
    } catch {
      setMessage(t('reglages.horsLigne.erreur'));
    }
  }
  const liste = (reglage?.perimetre ?? [])
    .map((ressource, index) => {
      const nom = t(`commun:navigation.${ressource}`);
      return index === 0 ? nom : nom.toLocaleLowerCase(langue);
    })
    .join(', ');
  return (
    <section className={styles.page} data-ui="offline-settings">
      <h1 className={styles.titre} data-ui="page-heading">
        {t('reglages.horsLigne.titre')}
      </h1>
      <p className={styles.sousTitre}>{t('reglages.horsLigne.sousTitre')}</p>
      <div className={styles.carte} data-ui="offline-state">
        <p>{t('reglages.horsLigne.perimetre', { liste })}</p>
        <p>{t('reglages.horsLigne.duree', { heures: reglage?.dureeMaxHeures ?? 0 })}</p>
        <p>
          {etat.data
            ? t('reglages.horsLigne.enregistre', {
                date: date(etat.data.enregistreLe),
                expire: date(etat.data.expireLe),
                nombre: etat.data.requetes,
              })
            : t('reglages.horsLigne.aucun')}
        </p>
        <p className={styles.sousTitre}>{t('reglages.horsLigne.jamais')}</p>
        <p className={styles.sousTitre}>{t('reglages.horsLigne.chiffrement')}</p>
        {progression ? (
          <label>
            {t('reglages.horsLigne.preparation', { ...progression })}
            <progress value={progression.faites} max={progression.total || 1} />
          </label>
        ) : null}
        <div className={styles.actions}>
          <Button
            disabled={
              !reglage || !enLigne || session.consultationHorsLigne || Boolean(progression) || demo
            }
            onClick={() => void preparer()}
          >
            {t('reglages.horsLigne.preparer')}
          </Button>
          <Button
            variant="danger"
            disabled={Boolean(progression) || demo}
            onClick={() => void effacer()}
          >
            {t('reglages.horsLigne.effacer')}
          </Button>
        </div>
        {message ? <p role="status">{message}</p> : null}
      </div>
    </section>
  );
}
